import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calculator, Sparkles, PlayCircle } from "lucide-react";
import { TID } from "@/constants/testIds";
import { LiveFeedCard } from "./LiveFeedCard";
import DemoDialog from "./DemoDialog";

const DOTS = {
  backgroundImage: "radial-gradient(circle, #d6d3d1 1px, transparent 1px)",
  backgroundSize: "22px 22px",
};

const KPIS = [
  { val: "100%", label: "Portaria 671 & ANVISA", sub: "Auditabilidade total" },
  { val: "-74%", label: "Desperdício PVPS", sub: "Impressão de etiquetas para estoque" },
  { val: "3 min", label: "Checklist de abertura", sub: "Foto IA + GPS verificado" },
];

export default function Hero() {
  const [demoOpen, setDemoOpen] = useState(false);
  return (
    <section data-testid={TID.landingHero} className="relative overflow-hidden" style={DOTS}>
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
      <div className="absolute inset-0 bg-gradient-to-b from-stone-50/40 via-transparent to-stone-50" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-24 lg:pt-20 lg:pb-32 grid grid-cols-[minmax(0,1fr)] lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7 min-w-0">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 pl-1.5 pr-3 py-1">
            <span className="rounded-full bg-emerald-800 text-white text-[10px] font-bold px-2 py-0.5 font-body uppercase tracking-wider">Novo</span>
            <span className="font-body text-xs text-emerald-900">Registro de ponto com Totem Facial</span>
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.98] text-stone-950">
            Cozinha padronizada.
            <br />
            <span className="text-emerald-800">Zero papel, zero desculpas.</span>
          </h1>
          <p className="mt-6 font-body text-base md:text-lg text-stone-600 leading-relaxed max-w-2xl">
            Sistema para food service que une <b className="text-stone-900">checklists com registro de foto e checagem com IA e GPS</b>,{" "}
            <b className="text-stone-900">controle de ponto com totem facial</b> e{" "}
            <b className="text-stone-900">controle de estoque com etiquetas térmicas</b> e baixa pela câmera do celular.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button className="bg-emerald-800 hover:bg-emerald-900 text-white font-body px-6 h-12 rounded-lg" data-testid={TID.landingCtaPrimary}>
                Iniciar trial gratuito <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Button
              variant="outline"
              className="border-stone-300 bg-white hover:bg-stone-100 font-body px-5 h-12 rounded-lg group"
              onClick={() => setDemoOpen(true)}
              data-testid="hero-demo-button"
            >
              <PlayCircle className="h-4 w-4 mr-2 text-emerald-700 group-hover:scale-110 transition-transform" /> Veja como funciona
            </Button>
            <Link to="/pricing">
              <Button variant="ghost" className="font-body px-5 h-12 rounded-lg text-stone-700 hover:text-stone-950 hover:bg-stone-100" data-testid={TID.landingCtaSecondary}>
                Ver planos
              </Button>
            </Link>
            <a href="#roi">
              <Button variant="ghost" className="font-body px-5 h-12 rounded-lg text-emerald-800 hover:text-emerald-900 hover:bg-emerald-50" data-testid="hero-roi-link">
                <Calculator className="h-4 w-4 mr-2" /> Calcular economia
              </Button>
            </a>
          </div>

          <dl className="mt-12 grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl">
            {KPIS.map((k) => (
              <div key={k.label} className="min-w-0 border-l-2 border-emerald-700 pl-3 sm:pl-4">
                <dd className="font-display text-2xl sm:text-4xl font-black text-stone-950 tracking-tight">{k.val}</dd>
                <dt className="mt-1 text-xs font-bold text-stone-800 font-body">{k.label}</dt>
                <div className="text-[11px] text-stone-500 font-body">{k.sub}</div>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5 lg:pl-6 pb-8 min-w-0">
          <LiveFeedCard />
        </div>
      </div>
      <div className="relative border-t border-stone-200 bg-white/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs text-stone-500 font-body">
          <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-emerald-700" /> Feito para organizar cozinhas que vivem no caos.</span>
          <span className="hidden md:inline">APPCC · RDC 216 · ISO 22000 · Portaria 671</span>
        </div>
      </div>
    </section>
  );
}
